import { formatCurrency, formatCurrencyCompact } from '@/lib/utils';

/**
 * Crew pay types, in one place.
 *
 * Every surface that shows or computes crew pay reads this module — the
 * compensation form, the team roster badge, the time-tab payroll estimate, the
 * payroll CSV, and the crew's own settings page. The unit ("/hr", "/day",
 * "/ft") is the part that used to be hardcoded next to `hourly_rate` in five
 * places; when a member is paid per day, a hardcoded "/hr" is not a cosmetic
 * bug, it misstates what someone is owed.
 *
 * Columns are per-unit on purpose (see migration 20260820014800): the rate a
 * member is on is `rateColumn` of their `pay_type`, and nothing else on the row
 * is meaningful pay data.
 */

export const PAY_TYPES = [
  'hourly',
  'daily',
  'salary',
  'commission',
  'per_linear_foot',
  'contract',
  'custom',
] as const;

export type PayType = (typeof PAY_TYPES)[number];

/** The pay-bearing columns of `team_profiles`, as the app reads them. */
export type PayMethodBasis = 'per_hour' | 'per_day' | 'per_foot' | 'per_job' | 'flat';
export interface OrgPayMethod {
  id: string;
  organization_id: string;
  name: string;
  unit_label: string;
  rate_basis: PayMethodBasis;
  is_active: boolean;
}

export interface PayFields {
  organization_id?: string;
  pay_method_id?: string | null;
  pay_method_rate?: number | string | null;
  /** PostgREST join: pay_method:org_pay_methods(*). Include inactive assigned methods. */
  pay_method?: OrgPayMethod | null;
  pay_type?: string | null;
  hourly_rate?: number | string | null;
  daily_rate?: number | string | null;
  salary_annual?: number | string | null;
  commission_percent?: number | string | null;
  rate_per_linear_foot?: number | string | null;
  custom_pay_label?: string | null;
  custom_pay_rate?: number | string | null;
}

/** Overlay the stored legacy shape without mutating it. Missing method data stays unpriced. */
export function resolvePayMethod(profile: PayFields, orgMethods: readonly OrgPayMethod[] = profile.pay_method ? [profile.pay_method] : []) {
  const method = profile.pay_method_id ? orgMethods.find(m => m.id === profile.pay_method_id
    && (!profile.organization_id || m.organization_id === profile.organization_id)) : undefined;
  if (!profile.pay_method_id) return { ...profile, label: customPayLabel(profile) ?? payTypeDef(profile.pay_type).label,
    unit: payTypeDef(profile.pay_type).unit, basis: null as PayMethodBasis | null };
  const type: PayType = method?.rate_basis === 'per_hour' ? 'hourly' : method?.rate_basis === 'per_day' ? 'daily'
    : method?.rate_basis === 'per_foot' ? 'per_linear_foot' : 'custom';
  const column = payTypeDef(type).rateColumn!;
  return { ...profile, pay_type: type, pay_method: method ?? null,
    [column]: method ? profile.pay_method_rate : null,
    label: method?.name ?? profile.custom_pay_label ?? 'Custom', unit: method?.unit_label ?? '',
    basis: method?.rate_basis ?? null };
}

/** Columns a compensation save must send, so an unused unit is never left stale. */
export const PAY_RATE_COLUMNS = [
  'hourly_rate',
  'daily_rate',
  'salary_annual',
  'commission_percent',
  'rate_per_linear_foot',
  'custom_pay_rate',
] as const;

export interface PayTypeDef {
  value: PayType;
  /** Pill / select label. */
  label: string;
  /** One-line explanation under the picker. */
  hint: string;
  /** Column holding this type's number; null for types that carry no rate. */
  rateColumn: (typeof PAY_RATE_COLUMNS)[number] | null;
  /** Field label in the compensation form. */
  rateLabel: string;
  /** Unit suffix printed after the amount ("/hr"). Empty when the unit is the value itself. */
  unit: string;
  /** How the number reads: money, or a percentage. */
  kind: 'currency' | 'percent';
  placeholder: string;
  /**
   * True when `hours worked × rate` is a truthful payroll number. Only hourly
   * qualifies — a daily or per-foot rate has no hours in it, and multiplying
   * one by clocked hours would invent a figure.
   */
  payableByHour: boolean;
}

const DEFS: Record<PayType, PayTypeDef> = {
  hourly: {
    value: 'hourly',
    label: 'Hourly',
    hint: 'Paid for every hour clocked.',
    rateColumn: 'hourly_rate',
    rateLabel: 'Hourly rate',
    unit: '/hr',
    kind: 'currency',
    placeholder: '25.00',
    payableByHour: true,
  },
  daily: {
    value: 'daily',
    label: 'Daily',
    hint: 'A flat day rate, however long the day runs.',
    rateColumn: 'daily_rate',
    rateLabel: 'Daily rate',
    unit: '/day',
    kind: 'currency',
    placeholder: '220.00',
    payableByHour: false,
  },
  salary: {
    value: 'salary',
    label: 'Salary',
    hint: 'A fixed annual figure, paid on your normal cycle.',
    rateColumn: 'salary_annual',
    rateLabel: 'Annual salary',
    unit: '/yr',
    kind: 'currency',
    placeholder: '65000',
    payableByHour: false,
  },
  commission: {
    value: 'commission',
    label: 'Commission',
    hint: 'A percentage of the work they close or complete.',
    rateColumn: 'commission_percent',
    rateLabel: 'Commission',
    unit: '',
    kind: 'percent',
    placeholder: '10',
    payableByHour: false,
  },
  per_linear_foot: {
    value: 'per_linear_foot',
    label: 'Per linear foot',
    hint: 'A piece rate for every foot installed.',
    rateColumn: 'rate_per_linear_foot',
    rateLabel: 'Rate per linear foot',
    unit: '/ft',
    kind: 'currency',
    placeholder: '3.50',
    payableByHour: false,
  },
  contract: {
    value: 'contract',
    label: 'Contract',
    hint: 'Terms live in the signed contract, not here.',
    rateColumn: null,
    rateLabel: '',
    unit: '',
    kind: 'currency',
    placeholder: '',
    payableByHour: false,
  },
  custom: {
    value: 'custom',
    label: 'Custom',
    hint: 'Name your own unit — per install, per fixture, per tree.',
    rateColumn: 'custom_pay_rate',
    rateLabel: 'Rate',
    unit: '',
    kind: 'currency',
    placeholder: '150.00',
    payableByHour: false,
  },
};

export function isPayType(value: unknown): value is PayType {
  return typeof value === 'string' && (PAY_TYPES as readonly string[]).includes(value);
}

/**
 * The definition for a stored `pay_type`. Falls back to hourly, which is both
 * the column default and how every row that predates the pay_type column reads.
 */
export function payTypeDef(value: unknown): PayTypeDef {
  return isPayType(value) ? DEFS[value] : DEFS.hourly;
}

function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** The rate this member is actually on, or null when there is none to show. */
export function payRateValue(fields: PayFields): number | null {
  if (fields.pay_method_id) return resolvePayMethod(fields).basis ? toNumber(fields.pay_method_rate) : null;
  const def = payTypeDef(fields.pay_type);
  if (!def.rateColumn) return null;
  return toNumber(fields[def.rateColumn]);
}

/** Custom unit label, trimmed; null when the member is not on a custom type. */
export function customPayLabel(fields: PayFields): string | null {
  if (payTypeDef(fields.pay_type).value !== 'custom') return null;
  const label = fields.custom_pay_label?.trim();
  return label || null;
}

/**
 * The rate as one readable string — "$25.00/hr", "$220.00/day", "10%",
 * "$150.00 · per install". Returns null when there is no rate to state
 * (contract, or a type whose rate has never been filled in); callers render
 * their own "—" so an empty rate never surfaces as `$NaN`.
 */
export function formatPayRate(fields: PayFields, orgMethods?: readonly OrgPayMethod[]): string | null {
  if (fields.pay_method_id) {
    const method = resolvePayMethod(fields, orgMethods);
    const value = payRateValue(method);
    return value === null ? null : `${formatCurrency(value)} · ${method.unit}`;
  }
  const def = payTypeDef(fields.pay_type);
  const value = payRateValue(fields);
  if (value === null) return null;

  if (def.kind === 'percent') return `${value}%`;

  const amount = formatCurrency(value);
  if (def.value === 'custom') {
    const label = customPayLabel(fields);
    return label ? `${amount} · ${label}` : amount;
  }
  return `${amount}${def.unit}`;
}

/**
 * Compact form for a roster badge: no cents on whole amounts, salary shortened
 * ("$65k/yr") so the chip does not have to truncate — the no-truncation rule.
 */
export function formatPayRateCompact(fields: PayFields): string | null {
  if (fields.pay_method_id) {
    // Same compact shape as the built-ins ("$220/day"), using the method's own unit.
    const method = resolvePayMethod(fields);
    const rate = payRateValue(method);
    if (rate === null) return null;
    const short = formatCurrencyCompact(rate);
    return method.unit ? `${short}/${method.unit}` : short;
  }
  const def = payTypeDef(fields.pay_type);
  const value = payRateValue(fields);
  if (value === null) return null;

  if (def.kind === 'percent') return `${value}%`;

  if (def.value === 'salary') {
    const short = value >= 1000 ? `$${Math.round(value / 1000)}k` : `$${value.toFixed(0)}`;
    return `${short}${def.unit}`;
  }

  const amount = formatCurrencyCompact(value);
  if (def.value === 'custom') {
    const label = customPayLabel(fields);
    return label ? `${amount} · ${label}` : amount;
  }
  return `${amount}${def.unit}`;
}

/**
 * The hourly rate to multiply clocked hours by, or null when the member's pay
 * type has no hourly meaning. Every payroll estimate in the app goes through
 * here so that a salaried or per-foot member is reported as "not hourly"
 * instead of silently valued at $0 — or worse, at a stale hourly_rate left over
 * from before they changed pay types.
 */
export function hourlyPayrollRate(fields: PayFields): number | null {
  fields = resolvePayMethod(fields);
  const def = payTypeDef(fields.pay_type);
  if (!def.payableByHour) return null;
  const rate = toNumber(fields.hourly_rate);
  return rate === null ? null : rate;
}
